import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import HomePage from "@/pages/HomePage";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AnalysePage from "@/pages/AnalysePage";
import HashtagCheckPage from "@/pages/HashtagCheckPage";
import PostCheckPage from "@/pages/PostCheckPage";
import CommentGoldminePage from "@/pages/CommentGoldminePage";
import TrendsHubPage from "@/pages/TrendsHubPage";
import FacebookHubPage from "@/pages/FacebookHubPage";
import AffiliateInvitePage from "@/pages/AffiliateInvitePage";
import LegalPage from "@/pages/LegalPage";
import BrandDnaPage from "@/pages/BrandDnaPage";
import AnalysisHistoryPage from "@/pages/AnalysisHistoryPage";
import StoryCheckPage from "@/pages/StoryCheckPage";
import CarouselPage from "@/pages/CarouselPage";
import CarouselMasterPage from "@/pages/CarouselMasterPage";
import ContentGeneratorPage from "@/pages/ContentGeneratorPage";
import AdminUsersPage from "@/pages/AdminUsersPage";
import { Loader2 } from "lucide-react";

axios.defaults.withCredentials = true;

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

// Gatekeeper for /dashboard:
// Yearly-plan members see the Affiliate-Onboarding ONCE on first arrival,
// then dismiss-flag prevents future redirects (page still reachable from menu).
function DashboardGate({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  if (user.plan === "yearly" && !user.affiliate_onboarding_dismissed) {
    return <Navigate to="/affiliate-invite" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
    </div>
  );
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<PublicOnly><HomePage /></PublicOnly>} />
            <Route path="/auth" element={<PublicOnly><AuthPage /></PublicOnly>} />
            <Route path="/dashboard" element={<DashboardGate><LandingPage /></DashboardGate>} />
            <Route path="/affiliate-invite" element={<ProtectedRoute><AffiliateInvitePage /></ProtectedRoute>} />
            <Route path="/brand-dna" element={<ProtectedRoute><BrandDnaPage /></ProtectedRoute>} />
            <Route path="/analyses" element={<ProtectedRoute><AnalysisHistoryPage /></ProtectedRoute>} />
            <Route path="/tools/story-check" element={<ProtectedRoute><StoryCheckPage /></ProtectedRoute>} />
            <Route path="/content" element={<ProtectedRoute><ContentGeneratorPage /></ProtectedRoute>} />
            <Route path="/carousel" element={<ProtectedRoute><CarouselMasterPage /></ProtectedRoute>} />
            <Route path="/carousel-legacy" element={<ProtectedRoute><CarouselPage /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><AdminUsersPage /></ProtectedRoute>} />
            <Route path="/impressum" element={<LegalPage slug="impressum" />} />
            <Route path="/agb" element={<LegalPage slug="agb" />} />
            <Route path="/datenschutz" element={<LegalPage slug="datenschutz" />} />
            <Route path="/analyse" element={<AnalysePage />} />
            <Route path="/hashtag-check" element={<ProtectedRoute><HashtagCheckPage /></ProtectedRoute>} />
            <Route path="/post-check" element={<ProtectedRoute><PostCheckPage /></ProtectedRoute>} />
            <Route path="/comment-goldmine" element={<ProtectedRoute><CommentGoldminePage /></ProtectedRoute>} />
            <Route path="/trends" element={<ProtectedRoute><TrendsHubPage /></ProtectedRoute>} />
            <Route path="/facebook-tools" element={<ProtectedRoute><FacebookHubPage /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      <Toaster
        position="bottom-right"
        toastOptions={{
          style: {
            background: '#18181b',
            border: '1px solid #27272a',
            color: '#ffffff',
          },
        }}
      />
    </div>
  );
}

export default App;
